import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Skeleton, Typography, useMediaQuery } from "@mui/material"
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link'
import { HiOutlineSearch } from "react-icons/hi";
import BaseIconInput from "../../BaseIconInput";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MedicationAccordion from "../../MedicationAccordion";
import { setMedicationFilterOptions } from "../../../store";
import BaseAccordion from "../../BaseAccordion";
import { HOW_IT_WORKS_ARTICLES, REPORTS_VIDEO_LIST } from "../../../constants";
import GeneButtonList from "../../GeneButtonList";

import { openModal } from "../../../store"

const RISK_LIST = [
  {
    score: 4,
    level: 'high',
    label: 'High Risk',
    description: 'Medications that you should avoid',
    color: 'primary.red'
  },
  {
    score: 7,
    level: 'moderate',
    label: 'Moderate Risk',
    description: 'Medications that you need to use with monitoring',
    color: 'primary.yellow'
  },
  {
    score: 48,
    level: 'low',
    label: 'Low Risk',
    description: 'Medications to use with standard precautions',
    color: 'primary.darkGreen'
  },
]

export const MEDICATION_LIST = [
  {
    title: 'Escitalopram',
    subTitle: 'Psychotropics',
    riskFactor: 'High',
    value: 100,
    descriptionTitle: 'CYP2C19 Poor metabolism',
    description: [
      'Reduced metabolism of citalopram and escitalopram to less active compounds. Higher plasma concentrations may increase the probability of side effects.', 
      'Consider a clinically appropriate antidepressant not predominantly metabolized by CYP2C19. If citalopram or escitalopram are clinically appropriate, consider a lower starting dose, slower titration schedule and 50% reduction of the standard maintenance dose as compared to normal metabolizers.',
      'Per the FDA warning, citalopram 20 mg/day is the maximum recommended dose in CYP2C19 poor metabolizers due to the risk of QT prolongation. FDA product labeling additionally cautions that citalopram dose should be limited to 20 mg/day in patients with hepatic impairment, those taking a CYP2C19 inhibitor, and patients greater than 60 years of age.'
    ],
    medicines: ['Lexapro'],
    gene: 'CYP2C19',
    class: 'Antidepressants',
  },
  {
    title: 'Rosuvastatin',
    subTitle: 'Cardiovascular',
    riskFactor: 'High',
    value: 90,
    medicines: ['Crestor', 'Rovartal', 'Rosimol', '+1'],
    gene: 'ABCG2',
    class: 'Lipid Modifying Agents',
    descriptionTitle: 'ABCG2 Poor function',
    description: [
      'Increased rosuvastatin exposure compared to normal. Based on ABCG2 status, prescribe ≤20mg as a starting dose and adjust doses of rosuvastatin based on disease-specific and specific population guidelines. If dose >20mg needed for desired efficacy, consider an alternative statin or combination therapy (i.e., rosuvastatin plus non-statin guideline directed medical therapy).', 
      'The potential for drug-drug interactions and dose limits based on renal and hepatic function and Asian ancestry should be evaluated prior to initiating rosuvastatin. The effects of drug-drug interactions may be more pronounced resulting in a higher risk of myopathy.'
    ],
  },
  {
    title: 'Allopurinol',
    subTitle: 'Gastrointestinal',
    riskFactor: 'Moderate',
    value: 50,
    medicines: ['Prevacid'],
    gene: 'ABCG2',
    class: 'Alpha-blockers for benign prostatic hyperplasia',
    descriptionTitle: 'Allopurinol',
    description: [
      'Patients with this genotype and gout may have a better reponse when trated with applopurinol and may require a lower dose. Studies defind a good response as serum urate under 6 mg/dl. Other genetic and clinical factors may also influence response to and dose of aollopurinol.'
    ],
  },
  {
    title: 'Galantamine',
    subTitle: 'Gastrointestinal',
    riskFactor: 'Low',
    value: 25,
    medicines: ['Alloprin', 'Adenock', 'Allohexal', 'Lexapro'],
    gene: 'ABCG2',
    class: 'Alpha-blockers for benign prostatic hyperplasia',
    descriptionTitle: 'Galantamine',
    description: [
      'Patients with this genotype and gout may have a better reponse when trated with applopurinol and may require a lower dose. Studies defind a good response as serum urate under 6 mg/dl. Other genetic and clinical factors may also influence response to and dose of aollopurinol.', 
    ],
  },
]

const INFO_LIST = [
  {
    title: 'Test info'
  },
  {
    title: 'Legal information'
  },
];

const selectedArticle = HOW_IT_WORKS_ARTICLES.find(item => item.label === 'Medications');

const MedicationSummary = ({ onClickMedication, medicationData, medications, onClickSearch, onChangeTab }) => {
  const lg = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const dispatch = useDispatch()
  const loadingAuth = useSelector(state => state.loadingAuth)
  const filterOptions = useSelector(state => state.medicationFilterOptions)
  const [searchKey, setSearchKey] = useState('')

  const [filteredMedications, setFilteredMedications] = useState([])

  useMemo(() => {
    if (medications) {
      if (filterOptions?.guidanceLevelSortDir) {
        const sortDir = filterOptions.guidanceLevelSortDir
        setFilteredMedications(sortDir === 'asc' ? medications.sort((a, b) => a.guidanceLevel.localeCompare(b.guidanceLevel)) : medications.sort((a, b) => b.guidanceLevel.localeCompare(a.guidanceLevel)))
      } else {
        setFilteredMedications(medications)
      }
    }
  }, [medications, filterOptions?.guidanceLevelSortDir])

  const handleClickSearch = () => {
    if(searchKey !== '') {
      onClickSearch(searchKey)
    }
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      // 👇 Get input value
      handleClickSearch()
    }
  };

  const riskCounts = useMemo(() => {
    if (medicationData?.data?.counts) {
      const counts = medicationData?.data?.counts
      return RISK_LIST.map((item => (
        {
          ...item,
          score: counts[item.level],
          level: item.level
        }
      )))
    } else return []
  }, [medicationData])

  const handleClickRiskFactor = () => {
    dispatch(setMedicationFilterOptions({
      ...filterOptions,
      guidanceLevelSortDir: filterOptions.guidanceLevelSortDir === 'asc' ? 'desc' : 'asc'
    }))
  }

  const openVideoModal = (e) => {
    e.stopPropagation()
    dispatch(
      openModal({
        currentModal: 'REPORT_VIDEO_SUMMARY',
        modalProps: {
          videoUrl: REPORTS_VIDEO_LIST['Medications']
        }
    }))
  }

  return (
    <Box>
      <Box
        sx={{
          padding: '20px 16px',
          borderRadius: '8px',
          bgcolor: 'white',
          boxShadow: '0px 2px 10px 0px #5B87EF33',
          position: 'relative',
          zIndex: 3
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h2">
            Medication
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={onChangeTab}
          >
            <Typography variant="body">
              View all {!lg && 'medications'}
            </Typography>
            <ArrowForwardIosIcon
              sx={{
                fontSize: '16px',
                ml: '8px'
              }}
            />
          </Box>
        </Box>
        <Box sx={{ mt: '23px' }}>
          <BaseIconInput
            height='48'
            placeholder='Search'
            inputStyles={{
              background: '#4266B833',
              borderRadius: '40px',
              color: '#46545F',
              border: '#4266b800'
            }}
            sx={{
              maxWidth: '400px'
            }}
            startIcon={<HiOutlineSearch size={20} />}
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            onClickStartIcon={handleClickSearch}
            onKeyDown={handleKeyDown}
          />
        </Box>
        <Grid container spacing={2} mt="16px">
          {loadingAuth && (
            <>
            {new Array(3).fill(null).map((_, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Skeleton variant="rounded" width={'100%'} height={106} />
              </Grid>
            ))}
            </>
          )}
          {!loadingAuth && riskCounts.map((risk => (
            <Grid item xs={12} lg={4} key={risk.level}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '3px solid #fff',
                  borderTopColor: risk.color,
                  borderLeftColor: risk.color,
                  bgcolor: 'white',
                  minHeight: '106px',
                  cursor: 'pointer',
                  boxShadow: '0px 2px 10px 0px #5B87EF33',

                  '&:active': {
                    borderColor: risk.color
                  }
                }}
                onClick={() => onClickMedication(risk)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end'
                      }}
                    >
                      <Typography variant="h1">
                        {risk.score}
                      </Typography>
                      <Typography variant="h3" ml="4px">
                        {risk.label}
                      </Typography>
                    </Box>
                    <Typography variant="subText" color="primary.darkGray">
                      {risk.description}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      cursor: 'pointer',
                      mr: '16px'
                    }}
                  >
                    <ArrowForwardIosIcon 
                      sx={{
                        color: 'primary.black',
                        width: '18px'
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>
          )))}
        </Grid>
        <Box
          sx={{
            bgcolor: 'primary.lightGray',
            height: '1px',
            width: '100%',
            my: '16px'
          }}
        >
        </Box>
        <Box>
          <Typography variant="subText" color="primary.darkGray">
            This report includes guideline recommendations compiled by NIH-funded PharmGKB from the Clinical Pharmacogenetics Implementation Consortium, the Dutch Pharmacogenetics Working Group, and wording from the FDA's approved drug labels and Table of Pharmacogenetic Associations, based on individual genotypes. Before taking action on any information in this report, you must consult your doctor and pharmacist.
          </Typography>
          <Box
            sx={{
              mt: '16px'
            }}
          >
            <BaseAccordion
              isExpand={false}
              isExpandIcon
              item={{
                title: 'What was tested'
              }}
              sx={{
                bgcolor: 'primary.lightGray',
                boxShadow: '0px'
              }}
            >
              <Box sx={{mb: '14px'}}>
                <Link sx={{cursor: 'pointer'}} onClick={openVideoModal}>
                  <Typography variant='body'>
                    Video Summary
                  </Typography>
                </Link>
              </Box>
              <Typography
                variant="body"
                color="primary.darkGray"
                whiteSpace='pre-wrap'
              >
                {selectedArticle?.description}
              </Typography>
              {selectedArticle?.genes && (
                <Typography
                  variant="body"
                  color="primary.darkGray"
                  whiteSpace='pre-wrap'
                >
                  <GeneButtonList genes={selectedArticle.genes} styleProps={{
                    fontSize: '15px',
                    fontWeight: '500',
                  }}/>
                </Typography>
              )}
            </BaseAccordion>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          mt: '56px'
        }}
      >
        <Typography variant="h2">
          Your current medications
        </Typography>
        <Box
          sx={{
            backgroundColor: 'primary.darkGray',
            boxShadow: '0px 2px 10px 0px #5B87EF33',
            height: '64px',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '4px',
            padding: '16px',
            mt: '24px',
            mb: "16px"
          }}
        >
          <Typography variant="h3" color="white">
            Drug name
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={handleClickRiskFactor}
          >
            <Typography variant="body" color="white">
              Risk factor
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
                ml: '4px'
              }}
            >
              <ExpandLessIcon
                sx={{
                  color: filterOptions?.guidanceLevelSortDir ? filterOptions?.guidanceLevelSortDir === 'asc' ? 'white' : 'primary.mainBlue' : 'white',
                  fontSize: filterOptions?.guidanceLevelSortDir && filterOptions?.guidanceLevelSortDir === 'asc' ? '14px' : '15px'
                }}
              />
              <ExpandMoreIcon
                sx={{
                  color: filterOptions?.guidanceLevelSortDir ? filterOptions?.guidanceLevelSortDir === 'desc' ? 'white' : 'primary.mainBlue' : 'white',
                  fontSize: filterOptions?.guidanceLevelSortDir && filterOptions?.guidanceLevelSortDir === 'desc' ? '14px' : '15px'
                }}
              />
            </Box>
          </Box>
        </Box>
        <Grid container spacing={'8px'} mt='24px'>
          {filteredMedications && filteredMedications.map(((item, idx) => (
            <Grid item xs={12} key={`${item.title}-${idx}`}>
              <MedicationAccordion item={item} />
            </Grid>
          )))}
        </Grid>
      {(!filteredMedications || filteredMedications.length === 0) && (
        <Box sx={{
          mt: '16px',
          textAlign: 'center'
        }}>
          <Typography variant="body">No medications</Typography>
        </Box>
      )}
      </Box>
      <Box
        sx={{
          mt: '56px',
          display: 'none'
        }}
      >
        <Typography variant="h2">
          Additional information
        </Typography>
        <Box
          sx={{
            mt: '24px'
          }}
        >
        {INFO_LIST.map((item, idx) => (
          <Accordion
            key={idx}
            sx={{
              width: '100%',
              marginBottom: "15px",
              backgroundColor: "primary.white",
              boxShadow: '0px 2px 10px 0px #5B87EF33'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography variant="h4" color="primary.black">{item.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body" color="primary.darkGray">{item.title}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
        </Box>
      </Box>
    </Box>
  )
}

export default MedicationSummary